/**
 * Downloads data as a CSV file
 * @param data Array of objects to export
 * @param filename Name for the downloaded file (without .csv extension)
 * @param options Optional configuration
 */
export function downloadCSV<T extends Record<string, any>>(
  data: T[],
  filename: string,
  options?: {
    /** Fields to flatten (e.g., ['contactPerson', 'image']) */
    nestedFields?: string[];
    /** CSV delimiter (default: ',') */
    delimiter?: string;
  }
) {
  // Set default options
  const { nestedFields = [], delimiter = ',' } = options || {};

  // Flatten nested objects
  const flattenedData = data.map((item) => {
    const flatItem: Record<string, any> = { ...item };

    nestedFields.forEach((field) => {
      if (item[field]) {
        Object.entries(item[field]).forEach(([key, value]) => {
          flatItem[`${field}_${key}`] = value;
        });
        delete flatItem[field];
      }
    });
    return flatItem;
  });

  // Get all unique keys from the data
  const headers = Array.from(
    new Set(flattenedData.flatMap((item) => Object.keys(item)))
  );

  // Create CSV content
  const csvContent = [
    headers.join(delimiter),
    ...flattenedData.map((item) =>
      headers
        .map((header) => JSON.stringify(item[header] ?? ''))
        .join(delimiter)
    )
  ].join('\r\n');

  // Create download link
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
